import { createSlice, Slice } from '@reduxjs/toolkit';
import { ISentenceListItem } from 'modules/Sentence/interfaces/sentence';
import { initPaginatedListData } from 'modules/shared/constants';
import { IPaginatedData } from 'modules/shared/interfaces/paginationMeta';
import { IReqStatus } from 'modules/shared/interfaces/requestStatus';
import { RootStateOrAny } from 'react-redux';
import { IBusyPictureLesson } from '../interfaces/busyPictureLesson';
import { IGrammarLesson } from '../interfaces/grammarLesson';
import { ILessonBase, ILessonListItem } from '../interfaces/lesson';
import { ILessonCategory } from '../interfaces/lessonCategory';
import { ILessonItemModifier } from '../interfaces/lessonItemModifier';
import { ILexicaryLesson } from '../interfaces/lexicaryLesson';
import { INumberCombinationLesson } from '../interfaces/numberCombinationLesson';
import { ISentenceLesson } from '../interfaces/sentenceLesson';
import { ISightWordsLesson } from '../interfaces/sightWordsLesson';
import { ISortingLesson } from '../interfaces/sortingLesson';
import { IVerbLesson } from '../interfaces/verbLesson';
import { IVocabLesson, IVocabLessonWord } from '../interfaces/vocabLesson';
import { ITimeLesson } from '../interfaces/timeLesson';
import { ISceneMatchingLesson } from '../interfaces/sceneMatchingLesson';

export const REGIONS_CONFIGURATION_KEY_IN_STORE = 'regions-configuration';

export interface IRegionsConfigurationState {
	isLoading: boolean;
	lessons: IPaginatedData<ILessonListItem>;
	searchLessonsAutoComplete: {
		isLoading: boolean;
		result: ILessonBase[];
	};
	deleteLesson: IReqStatus;
	newLessonOrder: {
		order: number;
		isLoading: boolean;
	};
	createVocabLesson: IReqStatus;
	updateVocabLesson: {
		itemToUpdate: {
			isLoading: boolean;
			data?: IVocabLesson;
			status: IReqStatus;
		};
		status: IReqStatus;
	};

	createVerbLesson: IReqStatus;
	updateVerbLesson: {
		itemToUpdate: {
			isLoading: boolean;
			data?: IVerbLesson;
			status: IReqStatus;
		};
		status: IReqStatus;
	};

	// On delete vocab lesson word
	getSentencesWithVocabLessonWord: {
		isLoading: boolean;
		sentences: ISentenceListItem[];
		status: IReqStatus;
	};

	// On delete vocab lesson
	getSentencesWithVocabLessonWords: {
		isLoading: boolean;
		sentences: ISentenceListItem[];
		status: IReqStatus;
	};

	// On update vocab lesson order
	getSentenceLessonsWithOrderLessThanVocabLessonNewOrder: {
		isLoading: boolean;
		sentences: ISentenceLesson[];
		status: IReqStatus;
	};

	createSentenceLesson: IReqStatus;
	updateSentenceLesson: {
		itemToUpdate: {
			isLoading: boolean;
			data?: ISentenceLesson;
			status: IReqStatus;
		};
		status: IReqStatus;
	};
	searchSentenceLessonVocabWordsAutoComplete: {
		isLoading: boolean;
		result: IVocabLessonWord[];
	};

	createLexicaryLesson: IReqStatus;
	updateLexicaryLesson: {
		itemToUpdate: {
			isLoading: boolean;
			data?: ILexicaryLesson;
			status: IReqStatus;
		};
		status: IReqStatus;
	};

	lessonItemModifiers: {
		isLoading: boolean;
		searchLessonItemModifiersAutoComplete: {
			isLoading: boolean;
			result: ILessonItemModifier[];
		};
		createLessonItemModifier: IReqStatus & {
			lessonItemModifier?: ILessonItemModifier;
		};
		updateLessonItemModifier: IReqStatus & {
			lessonItemModifier?: ILessonItemModifier;
		};
		deleteLessonItemModifier: IReqStatus;
	};

	createGrammarLesson: IReqStatus;
	updateGrammarLesson: {
		itemToUpdate: {
			isLoading: boolean;
			data?: IGrammarLesson;
			status: IReqStatus;
		};
		status: IReqStatus;
	};

	createSortingLesson: IReqStatus;
	updateSortingLesson: {
		itemToUpdate: {
			isLoading: boolean;
			data?: ISortingLesson;
			status: IReqStatus;
		};
		status: IReqStatus;
	};

	lessonCategories: IPaginatedData<ILessonCategory>;
	searchLessonCategoriesAutoComplete: {
		isLoading: boolean;
		result: ILessonCategory[];
	};
	createLessonCategory: {
		isLoading: boolean;
		status: IReqStatus;
	};

	updateLessonCategory: {
		isLoading: boolean;
		status: IReqStatus;
	};
	deleteLessonCategory: IReqStatus;

	createBusyPictureLesson: IReqStatus;
	updateBusyPictureLesson: {
		itemToUpdate: {
			isLoading: boolean;
			data?: IBusyPictureLesson;
			status: IReqStatus;
		};
		status: IReqStatus;
	};

	createNumberCombinationLesson: IReqStatus;
	updateNumberCombinationLesson: {
		itemToUpdate: {
			isLoading: boolean;
			data?: INumberCombinationLesson;
			status: IReqStatus;
		};
		status: IReqStatus;
	};

	createSightWordsLesson: IReqStatus;
	updateSightWordsLesson: {
		itemToUpdate: {
			isLoading: boolean;
			data?: ISightWordsLesson;
			status: IReqStatus;
		};
		status: IReqStatus;
	};

	createTimeLesson: IReqStatus;
	updateTimeLesson: {
		itemToUpdate: {
			isLoading: boolean;
			data?: ITimeLesson;
			status: IReqStatus;
		};
		status: IReqStatus;
	};

	searchVocabWordsAutoComplete: {
		isLoading: boolean;
		result: IVocabLessonWord[];
	};

	// --
	createSceneMatchingLesson: IReqStatus;
	updateSceneMatchingLesson: {
		itemToUpdate: {
			isLoading: boolean;
			data?: ISceneMatchingLesson;
			status: IReqStatus;
		};
		status: IReqStatus;
	};
}

const initialState: IRegionsConfigurationState = {
	isLoading: false,
	lessons: initPaginatedListData,
	searchLessonsAutoComplete: {
		isLoading: false,
		result: [],
	},
	deleteLesson: {
		success: false,
		error: false,
	},
	newLessonOrder: {
		order: 1,
		isLoading: false,
	},
	createVocabLesson: {
		success: false,
		error: false,
	},
	updateVocabLesson: {
		itemToUpdate: {
			isLoading: false,
			data: undefined,
			status: {
				success: false,
				error: false,
			},
		},
		status: {
			success: false,
			error: false,
		},
	},
	createVerbLesson: {
		success: false,
		error: false,
	},

	updateVerbLesson: {
		itemToUpdate: {
			isLoading: false,
			data: undefined,
			status: {
				success: false,
				error: false,
			},
		},
		status: {
			success: false,
			error: false,
		},
	},

	getSentencesWithVocabLessonWord: {
		isLoading: false,
		sentences: [],
		status: {
			success: false,
			error: false,
		},
	},

	getSentencesWithVocabLessonWords: {
		isLoading: false,
		sentences: [],
		status: {
			success: false,
			error: false,
		},
	},

	getSentenceLessonsWithOrderLessThanVocabLessonNewOrder: {
		isLoading: false,
		sentences: [],
		status: {
			success: false,
			error: false,
		},
	},

	createSentenceLesson: {
		success: false,
		error: false,
	},
	updateSentenceLesson: {
		itemToUpdate: {
			isLoading: false,
			data: undefined,
			status: {
				success: false,
				error: false,
			},
		},
		status: {
			success: false,
			error: false,
		},
	},

	searchSentenceLessonVocabWordsAutoComplete: {
		isLoading: false,
		result: [],
	},

	createLexicaryLesson: {
		success: false,
		error: false,
	},
	updateLexicaryLesson: {
		itemToUpdate: {
			isLoading: false,
			data: undefined,
			status: {
				success: false,
				error: false,
			},
		},
		status: {
			success: false,
			error: false,
		},
	},
	lessonItemModifiers: {
		isLoading: false,
		searchLessonItemModifiersAutoComplete: {
			isLoading: false,
			result: [],
		},
		createLessonItemModifier: {
			error: false,
			success: false,
		},
		updateLessonItemModifier: {
			error: false,
			success: false,
		},
		deleteLessonItemModifier: {
			error: false,
			success: false,
		},
	},

	createGrammarLesson: {
		success: false,
		error: false,
	},
	updateGrammarLesson: {
		itemToUpdate: {
			isLoading: false,
			data: undefined,
			status: {
				success: false,
				error: false,
			},
		},
		status: {
			success: false,
			error: false,
		},
	},

	createSortingLesson: {
		success: false,
		error: false,
	},
	updateSortingLesson: {
		itemToUpdate: {
			isLoading: false,
			data: undefined,
			status: {
				success: false,
				error: false,
			},
		},
		status: {
			success: false,
			error: false,
		},
	},

	lessonCategories: initPaginatedListData,
	searchLessonCategoriesAutoComplete: {
		isLoading: false,
		result: [],
	},
	createLessonCategory: {
		isLoading: false,
		status: {
			error: false,
			success: false,
		},
	},

	updateLessonCategory: {
		isLoading: false,
		status: {
			error: false,
			success: false,
		},
	},
	deleteLessonCategory: {
		error: false,
		success: false,
	},

	createBusyPictureLesson: {
		success: false,
		error: false,
	},
	updateBusyPictureLesson: {
		itemToUpdate: {
			isLoading: false,
			data: undefined,
			status: {
				success: false,
				error: false,
			},
		},
		status: {
			success: false,
			error: false,
		},
	},

	createNumberCombinationLesson: {
		success: false,
		error: false,
	},

	updateNumberCombinationLesson: {
		itemToUpdate: {
			isLoading: false,
			data: undefined,
			status: {
				success: false,
				error: false,
			},
		},
		status: {
			success: false,
			error: false,
		},
	},

	createSightWordsLesson: {
		success: false,
		error: false,
	},

	updateSightWordsLesson: {
		itemToUpdate: {
			isLoading: false,
			data: undefined,
			status: {
				success: false,
				error: false,
			},
		},
		status: {
			success: false,
			error: false,
		},
	},

	createTimeLesson: {
		success: false,
		error: false,
	},

	updateTimeLesson: {
		itemToUpdate: {
			isLoading: false,
			data: undefined,
			status: {
				success: false,
				error: false,
			},
		},
		status: {
			success: false,
			error: false,
		},
	},

	searchVocabWordsAutoComplete: {
		isLoading: false,
		result: [],
	},

	// --
	createSceneMatchingLesson: {
		success: false,
		error: false,
	},

	updateSceneMatchingLesson: {
		itemToUpdate: {
			isLoading: false,
			data: undefined,
			status: {
				success: false,
				error: false,
			},
		},
		status: {
			success: false,
			error: false,
		},
	},
};

const regionsConfigurationSlice: Slice = createSlice({
	name: REGIONS_CONFIGURATION_KEY_IN_STORE,
	initialState,
	reducers: {
		getLessons: state => {
			state.isLoading = true;
		},

		getLessonsSuccess: (state: IRegionsConfigurationState, action) => {
			state.isLoading = false;
			state.lessons = action.payload;
		},
		getLessonsFailure: (state: IRegionsConfigurationState) => {
			state.isLoading = false;
		},

		deleteLesson: (state: IRegionsConfigurationState) => {
			state.deleteLesson = {
				success: false,
				error: false,
			};
			state.isLoading = true;
		},
		deleteLessonSuccess: (state: IRegionsConfigurationState) => {
			state.deleteLesson = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		deleteLessonFailure: (state: IRegionsConfigurationState) => {
			state.deleteLesson = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		searchLessons: (state: IRegionsConfigurationState) => {
			state.searchLessonsAutoComplete = {
				isLoading: true,
				result: [],
			};
		},
		setSearchLessonsAutoCompleteResult: (
			state: IRegionsConfigurationState,
			action,
		) => {
			state.searchLessonsAutoComplete = {
				isLoading: false,
				result: action.payload.lessons,
			};
		},
		resetSetSearchLessonsAutoComplete: (state: IRegionsConfigurationState) => {
			state.searchLessonsAutoComplete = {
				isLoading: false,
				result: [],
			};
		},
		resetDeleteLessonStatus: (state: IRegionsConfigurationState) => {
			state.deleteLesson = {
				success: false,
				error: false,
			};
		},

		getNewLessonOrder: (state: IRegionsConfigurationState) => {
			state.newLessonOrder.isLoading = true;
		},
		setNewLessonOrder: (state: IRegionsConfigurationState, action) => {
			state.newLessonOrder = {
				isLoading: false,
				order: action.payload.order,
			};
		},

		createVocabLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		createVocabLessonSuccess: (state: IRegionsConfigurationState) => {
			state.createVocabLesson = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		createVocabLessonFailure: (state: IRegionsConfigurationState) => {
			state.createVocabLesson = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		resetRegionsConfigurationData: () => {
			return initialState;
		},

		getVocabLessonToUpdate: (state: IRegionsConfigurationState) => {
			state.updateVocabLesson.itemToUpdate = {
				isLoading: true,
				data: undefined,
				status: {
					success: false,
					error: false,
				},
			};
		},
		getVocabLessonToUpdateSuccess: (
			state: IRegionsConfigurationState,
			action,
		) => {
			state.updateVocabLesson.itemToUpdate = {
				isLoading: false,
				data: action.payload.lesson,
				status: {
					success: true,
					error: false,
				},
			};
		},
		getVocabLessonToUpdateFailure: (state: IRegionsConfigurationState) => {
			state.updateVocabLesson.itemToUpdate = {
				isLoading: false,
				data: undefined,
				status: {
					success: false,
					error: true,
				},
			};
		},

		updateVocabLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		updateVocabLessonSuccess: (state: IRegionsConfigurationState) => {
			state.updateVocabLesson.status = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		updateVocabLessonFailure: (state: IRegionsConfigurationState) => {
			state.updateVocabLesson.status = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		createVerbLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		createVerbLessonSuccess: (state: IRegionsConfigurationState) => {
			state.createVerbLesson = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		createVerbLessonFailure: (state: IRegionsConfigurationState) => {
			state.createVerbLesson = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		getVerbLessonToUpdate: (state: IRegionsConfigurationState) => {
			state.updateVerbLesson.itemToUpdate = {
				isLoading: true,
				data: undefined,
				status: {
					success: false,
					error: false,
				},
			};
		},
		getVerbLessonToUpdateSuccess: (
			state: IRegionsConfigurationState,
			action,
		) => {
			state.updateVerbLesson.itemToUpdate = {
				isLoading: false,
				data: action.payload.lesson,
				status: {
					success: true,
					error: false,
				},
			};
		},
		getVerbLessonToUpdateFailure: (state: IRegionsConfigurationState) => {
			state.updateVerbLesson.itemToUpdate = {
				isLoading: false,
				data: undefined,
				status: {
					success: false,
					error: true,
				},
			};
		},
		updateVerbLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		updateVerbLessonSuccess: (state: IRegionsConfigurationState) => {
			state.updateVerbLesson.status = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		updateVerbLessonFailure: (state: IRegionsConfigurationState) => {
			state.updateVerbLesson.status = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		getSentencesWithVocabLessonWord: (state: IRegionsConfigurationState) => {
			state.getSentencesWithVocabLessonWord.isLoading = true;
		},
		getSentencesWithVocabLessonWordSuccess: (
			state: IRegionsConfigurationState,
			action,
		) => {
			state.getSentencesWithVocabLessonWord = {
				isLoading: false,
				sentences: action.payload.sentences,
				status: {
					success: true,
					error: false,
				},
			};
		},
		getSentencesWithVocabLessonWordFailure: (
			state: IRegionsConfigurationState,
		) => {
			state.getSentencesWithVocabLessonWord = {
				isLoading: false,
				sentences: [],
				status: {
					success: false,
					error: true,
				},
			};
		},
		resetGetSentenceLessonsWithVocabWord: (
			state: IRegionsConfigurationState,
		) => {
			state.getSentencesWithVocabLessonWord =
				initialState.getSentencesWithVocabLessonWord;
		},

		getSentencesWithVocabLessonWords: (state: IRegionsConfigurationState) => {
			state.getSentencesWithVocabLessonWords.isLoading = true;
		},
		getSentencesWithVocabLessonWordsSuccess: (
			state: IRegionsConfigurationState,
			action,
		) => {
			state.getSentencesWithVocabLessonWords = {
				isLoading: false,
				sentences: action.payload.sentences,
				status: {
					success: true,
					error: false,
				},
			};
		},
		getSentencesWithVocabLessonWordsFailure: (
			state: IRegionsConfigurationState,
		) => {
			state.getSentencesWithVocabLessonWords = {
				isLoading: false,
				sentences: [],
				status: {
					success: false,
					error: true,
				},
			};
		},
		resetGetSentenceLessonsWithVocabLessonWords: (
			state: IRegionsConfigurationState,
		) => {
			state.getSentencesWithVocabLessonWords =
				initialState.getSentencesWithVocabLessonWords;
		},

		resetGetSentenceLessonsWithVocabLessonWordsStatus: (
			state: IRegionsConfigurationState,
		) => {
			state.getSentencesWithVocabLessonWords = {
				...state.getSentencesWithVocabLessonWords,
				status: initialState.getSentencesWithVocabLessonWords.status,
			};
		},

		getSentenceLessonsWithOrderLessThanVocabLessonNewOrder: (
			state: IRegionsConfigurationState,
		) => {
			state.getSentenceLessonsWithOrderLessThanVocabLessonNewOrder.isLoading =
				true;
		},
		getSentenceLessonsWithOrderLessThanVocabLessonNewOrderSuccess: (
			state: IRegionsConfigurationState,
			action,
		) => {
			state.getSentenceLessonsWithOrderLessThanVocabLessonNewOrder = {
				isLoading: false,
				sentences: action.payload.sentences,
				status: {
					success: true,
					error: false,
				},
			};
		},
		getSentenceLessonsWithOrderLessThanVocabLessonNewOrderFailure: (
			state: IRegionsConfigurationState,
		) => {
			state.getSentenceLessonsWithOrderLessThanVocabLessonNewOrder = {
				isLoading: false,
				sentences: [],
				status: {
					success: false,
					error: true,
				},
			};
		},
		resetGetSentenceLessonsWithOrderLessThanVocabLessonNewOrder: (
			state: IRegionsConfigurationState,
		) => {
			state.getSentenceLessonsWithOrderLessThanVocabLessonNewOrder =
				initialState.getSentenceLessonsWithOrderLessThanVocabLessonNewOrder;
		},

		createSentenceLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		createSentenceLessonSuccess: (state: IRegionsConfigurationState) => {
			state.createSentenceLesson = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		createSentenceLessonFailure: (state: IRegionsConfigurationState) => {
			state.createSentenceLesson = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		getSentenceLessonToUpdate: (state: IRegionsConfigurationState) => {
			state.updateSentenceLesson.itemToUpdate = {
				isLoading: true,
				data: undefined,
				status: {
					success: false,
					error: false,
				},
			};
		},
		getSentenceLessonToUpdateSuccess: (
			state: IRegionsConfigurationState,
			action,
		) => {
			state.updateSentenceLesson.itemToUpdate = {
				isLoading: false,
				data: action.payload.lesson,
				status: {
					success: true,
					error: false,
				},
			};
		},
		getSentenceLessonToUpdateFailure: (state: IRegionsConfigurationState) => {
			state.updateSentenceLesson.itemToUpdate = {
				isLoading: false,
				data: undefined,
				status: {
					success: false,
					error: true,
				},
			};
		},
		updateSentenceLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		updateSentenceLessonSuccess: (state: IRegionsConfigurationState) => {
			state.updateSentenceLesson.status = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		updateSentenceLessonFailure: (state: IRegionsConfigurationState) => {
			state.updateSentenceLesson.status = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		searchSentenceLessonVocabWords: (state: IRegionsConfigurationState) => {
			state.searchSentenceLessonVocabWordsAutoComplete = {
				isLoading: true,
				result: [],
			};
		},
		setSearchSentenceLessonVocabWordsAutoCompleteResult: (
			state: IRegionsConfigurationState,
			action,
		) => {
			state.searchSentenceLessonVocabWordsAutoComplete = {
				isLoading: false,
				result: action.payload.sentenceLessonVocabWords,
			};
		},
		resetSetSearchSentenceLessonVocabWordsAutoComplete: (
			state: IRegionsConfigurationState,
		) => {
			state.searchSentenceLessonVocabWordsAutoComplete = {
				isLoading: false,
				result: [],
			};
		},

		createLexicaryLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		createLexicaryLessonSuccess: (state: IRegionsConfigurationState) => {
			state.createLexicaryLesson = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		createLexicaryLessonFailure: (state: IRegionsConfigurationState) => {
			state.createLexicaryLesson = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		getLexicaryLessonToUpdate: (state: IRegionsConfigurationState) => {
			state.updateLexicaryLesson.itemToUpdate = {
				isLoading: true,
				data: undefined,
				status: {
					success: false,
					error: false,
				},
			};
		},
		getLexicaryLessonToUpdateSuccess: (
			state: IRegionsConfigurationState,
			action,
		) => {
			state.updateLexicaryLesson.itemToUpdate = {
				isLoading: false,
				data: action.payload.lesson,
				status: {
					success: true,
					error: false,
				},
			};
		},
		getLexicaryLessonToUpdateFailure: (state: IRegionsConfigurationState) => {
			state.updateLexicaryLesson.itemToUpdate = {
				isLoading: false,
				data: undefined,
				status: {
					success: false,
					error: true,
				},
			};
		},
		updateLexicaryLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		updateLexicaryLessonSuccess: (state: IRegionsConfigurationState) => {
			state.updateLexicaryLesson.status = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		updateLexicaryLessonFailure: (state: IRegionsConfigurationState) => {
			state.updateLexicaryLesson.status = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		searchLessonItemModifiers: (state: IRegionsConfigurationState) => {
			state.lessonItemModifiers.searchLessonItemModifiersAutoComplete = {
				isLoading: true,
				result: [],
			};
		},
		setSearchLessonItemModifiersResult: (
			state: IRegionsConfigurationState,
			action,
		) => {
			state.lessonItemModifiers.searchLessonItemModifiersAutoComplete = {
				isLoading: false,
				result: action.payload.lessonItemModifiers,
			};
		},
		resetSearchLessonItemModifiers: (state: IRegionsConfigurationState) => {
			state.lessonItemModifiers.searchLessonItemModifiersAutoComplete = {
				isLoading: false,
				result: [],
			};
		},

		createLessonItemModifier: (state: IRegionsConfigurationState) => {
			state.lessonItemModifiers.isLoading = true;
		},
		createLessonItemModifierSuccess: (
			state: IRegionsConfigurationState,
			action,
		) => {
			state.lessonItemModifiers.createLessonItemModifier = {
				success: true,
				error: false,
				lessonItemModifier: action.payload.data,
			};
			state.lessonItemModifiers.isLoading = false;
		},

		createLessonItemModifierFailure: (state: IRegionsConfigurationState) => {
			state.lessonItemModifiers.createLessonItemModifier = {
				success: false,
				error: true,
			};
			state.lessonItemModifiers.isLoading = false;
		},

		resetCreateLessonItemModifier: (state: IRegionsConfigurationState) => {
			state.lessonItemModifiers.createLessonItemModifier = {
				...state.lessonItemModifiers.createLessonItemModifier,
				success: false,
				error: false,
			};
			state.lessonItemModifiers.isLoading = false;
		},

		updateLessonItemModifier: (state: IRegionsConfigurationState) => {
			state.lessonItemModifiers.isLoading = true;
		},
		updateLessonItemModifierSuccess: (
			state: IRegionsConfigurationState,
			action,
		) => {
			state.lessonItemModifiers.updateLessonItemModifier = {
				success: true,
				error: false,
				lessonItemModifier: action.payload.data,
			};
			state.lessonItemModifiers.isLoading = false;
		},
		updateLessonItemModifierFailure: (state: IRegionsConfigurationState) => {
			state.lessonItemModifiers.updateLessonItemModifier = {
				success: false,
				error: true,
			};
			state.lessonItemModifiers.isLoading = false;
		},

		resetUpdateLessonItemModifier: (state: IRegionsConfigurationState) => {
			state.lessonItemModifiers.updateLessonItemModifier = {
				...state.lessonItemModifiers.updateLessonItemModifier,
				success: false,
				error: false,
			};
			state.lessonItemModifiers.isLoading = false;
		},

		deleteLessonItemModifier: (state: IRegionsConfigurationState) => {
			state.lessonItemModifiers.isLoading = true;
		},
		deleteLessonItemModifierSuccess: (state: IRegionsConfigurationState) => {
			state.lessonItemModifiers.deleteLessonItemModifier = {
				success: true,
				error: false,
			};
			state.lessonItemModifiers.isLoading = false;
		},
		deleteLessonItemModifierFailure: (state: IRegionsConfigurationState) => {
			state.lessonItemModifiers.deleteLessonItemModifier = {
				success: false,
				error: true,
			};
			state.lessonItemModifiers.isLoading = false;
		},

		resetDeleteLessonItemModifier: (state: IRegionsConfigurationState) => {
			state.lessonItemModifiers.deleteLessonItemModifier = {
				success: false,
				error: false,
			};
			state.lessonItemModifiers.isLoading = false;
		},

		createGrammarLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		createGrammarLessonSuccess: (state: IRegionsConfigurationState) => {
			state.createGrammarLesson = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		createGrammarLessonFailure: (state: IRegionsConfigurationState) => {
			state.createGrammarLesson = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		getGrammarLessonToUpdate: (state: IRegionsConfigurationState) => {
			state.updateGrammarLesson.itemToUpdate = {
				isLoading: true,
				data: undefined,
				status: {
					success: false,
					error: false,
				},
			};
		},
		getGrammarLessonToUpdateSuccess: (
			state: IRegionsConfigurationState,
			action,
		) => {
			state.updateGrammarLesson.itemToUpdate = {
				isLoading: false,
				data: action.payload.lesson,
				status: {
					success: true,
					error: false,
				},
			};
		},
		getGrammarLessonToUpdateFailure: (state: IRegionsConfigurationState) => {
			state.updateGrammarLesson.itemToUpdate = {
				isLoading: false,
				data: undefined,
				status: {
					success: false,
					error: true,
				},
			};
		},
		updateGrammarLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		updateGrammarLessonSuccess: (state: IRegionsConfigurationState) => {
			state.updateGrammarLesson.status = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		updateGrammarLessonFailure: (state: IRegionsConfigurationState) => {
			state.updateGrammarLesson.status = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		createSortingLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		createSortingLessonSuccess: (state: IRegionsConfigurationState) => {
			state.createSortingLesson = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		createSortingLessonFailure: (state: IRegionsConfigurationState) => {
			state.createSortingLesson = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		getSortingLessonToUpdate: (state: IRegionsConfigurationState) => {
			state.updateSortingLesson.itemToUpdate = {
				isLoading: true,
				data: undefined,
				status: {
					success: false,
					error: false,
				},
			};
		},
		getSortingLessonToUpdateSuccess: (
			state: IRegionsConfigurationState,
			action,
		) => {
			state.updateSortingLesson.itemToUpdate = {
				isLoading: false,
				data: action.payload.lesson,
				status: {
					success: true,
					error: false,
				},
			};
		},
		getSortingLessonToUpdateFailure: (state: IRegionsConfigurationState) => {
			state.updateSortingLesson.itemToUpdate = {
				isLoading: false,
				data: undefined,
				status: {
					success: false,
					error: true,
				},
			};
		},
		updateSortingLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		updateSortingLessonSuccess: (state: IRegionsConfigurationState) => {
			state.updateSortingLesson.status = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		updateSortingLessonFailure: (state: IRegionsConfigurationState) => {
			state.updateSortingLesson.status = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		getLessonCategories: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		getLessonCategoriesSuccess: (state: IRegionsConfigurationState, action) => {
			state.isLoading = false;
			state.lessonCategories = action.payload;
		},
		getLessonCategoriesFailure: (state: IRegionsConfigurationState) => {
			state.isLoading = false;
			state.lessonCategories = initialState.lessonCategories;
		},

		createLessonCategory: (state: IRegionsConfigurationState) => {
			state.createLessonCategory.isLoading = true;
		},

		createLessonCategorySuccess: (state: IRegionsConfigurationState) => {
			state.createLessonCategory = {
				isLoading: false,
				status: {
					error: false,
					success: true,
				},
			};
		},
		createLessonCategoryFailure: (state: IRegionsConfigurationState) => {
			state.createLessonCategory = {
				isLoading: false,
				status: {
					error: true,
					success: false,
				},
			};
		},
		resetCreateLessonCategory: (state: IRegionsConfigurationState) => {
			state.createLessonCategory = initialState.createLessonCategory;
		},

		updateLessonCategory: (state: IRegionsConfigurationState) => {
			state.updateLessonCategory.isLoading = true;
		},

		updateLessonCategorySuccess: (
			state: IRegionsConfigurationState,
			action,
		) => {
			const updatedLessonCategory = action.payload.data;
			state.lessonCategories.items = state.lessonCategories.items.map(
				lessonCategory =>
					lessonCategory.id === updatedLessonCategory.id
						? updatedLessonCategory
						: lessonCategory,
			);
			state.updateLessonCategory = {
				isLoading: false,
				status: {
					error: false,
					success: true,
				},
			};
		},
		updateLessonCategoryFailure: (state: IRegionsConfigurationState) => {
			state.updateLessonCategory = {
				isLoading: false,
				status: {
					error: true,
					success: false,
				},
			};
		},
		resetUpdateLessonCategory: (state: IRegionsConfigurationState) => {
			state.updateLessonCategory = initialState.updateLessonCategory;
		},

		deleteLessonCategory: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		deleteLessonCategorySuccess: (state: IRegionsConfigurationState) => {
			state.isLoading = false;
			state.deleteLessonCategory = {
				error: false,
				success: true,
			};
		},
		deleteLessonCategoryFailure: (state: IRegionsConfigurationState) => {
			state.isLoading = false;
			state.deleteLessonCategory = {
				error: true,
				success: false,
			};
		},
		resetDeleteLessonCategory: (state: IRegionsConfigurationState) => {
			state.deleteLessonCategory = {
				error: false,
				success: false,
			};
		},

		searchLessonCategories: (state: IRegionsConfigurationState) => {
			state.searchLessonCategoriesAutoComplete = {
				isLoading: true,
				result: [],
			};
		},
		setSearchLessonCategoriesResult: (
			state: IRegionsConfigurationState,
			action,
		) => {
			state.searchLessonCategoriesAutoComplete = {
				isLoading: false,
				result: action.payload.lessonCategories,
			};
		},
		resetSearchLessonCategories: (state: IRegionsConfigurationState) => {
			state.searchLessonCategoriesAutoComplete = {
				isLoading: false,
				result: [],
			};
		},

		createBusyPictureLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		createBusyPictureLessonSuccess: (state: IRegionsConfigurationState) => {
			state.createBusyPictureLesson = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		createBusyPictureLessonFailure: (state: IRegionsConfigurationState) => {
			state.createBusyPictureLesson = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		getBusyPictureLessonToUpdate: (state: IRegionsConfigurationState) => {
			state.updateBusyPictureLesson.itemToUpdate = {
				isLoading: true,
				data: undefined,
				status: {
					success: false,
					error: false,
				},
			};
		},
		getBusyPictureLessonToUpdateSuccess: (
			state: IRegionsConfigurationState,
			action,
		) => {
			state.updateBusyPictureLesson.itemToUpdate = {
				isLoading: false,
				data: action.payload.lesson,
				status: {
					success: true,
					error: false,
				},
			};
		},
		getBusyPictureLessonToUpdateFailure: (
			state: IRegionsConfigurationState,
		) => {
			state.updateBusyPictureLesson.itemToUpdate = {
				isLoading: false,
				data: undefined,
				status: {
					success: false,
					error: true,
				},
			};
		},
		updateBusyPictureLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		updateBusyPictureLessonSuccess: (state: IRegionsConfigurationState) => {
			state.updateBusyPictureLesson.status = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		updateBusyPictureLessonFailure: (state: IRegionsConfigurationState) => {
			state.updateBusyPictureLesson.status = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		createNumberCombinationLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		createNumberCombinationLessonSuccess: (
			state: IRegionsConfigurationState,
		) => {
			state.createNumberCombinationLesson = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		createNumberCombinationLessonFailure: (
			state: IRegionsConfigurationState,
		) => {
			state.createNumberCombinationLesson = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		getNumberCombinationLessonToUpdate: (state: IRegionsConfigurationState) => {
			state.updateNumberCombinationLesson.itemToUpdate = {
				isLoading: true,
				data: undefined,
				status: {
					success: false,
					error: false,
				},
			};
		},
		getNumberCombinationLessonToUpdateSuccess: (
			state: IRegionsConfigurationState,
			action,
		) => {
			state.updateNumberCombinationLesson.itemToUpdate = {
				isLoading: false,
				data: action.payload.lesson,
				status: {
					success: true,
					error: false,
				},
			};
		},
		getNumberCombinationLessonToUpdateFailure: (
			state: IRegionsConfigurationState,
		) => {
			state.updateNumberCombinationLesson.itemToUpdate = {
				isLoading: false,
				data: undefined,
				status: {
					success: false,
					error: true,
				},
			};
		},
		updateNumberCombinationLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		updateNumberCombinationLessonSuccess: (
			state: IRegionsConfigurationState,
		) => {
			state.updateNumberCombinationLesson.status = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		updateNumberCombinationLessonFailure: (
			state: IRegionsConfigurationState,
		) => {
			state.updateNumberCombinationLesson.status = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		createSightWordsLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		createSightWordsLessonSuccess: (state: IRegionsConfigurationState) => {
			state.createSightWordsLesson = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		createSightWordsLessonFailure: (state: IRegionsConfigurationState) => {
			state.createSightWordsLesson = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		getSightWordsLessonToUpdate: (state: IRegionsConfigurationState) => {
			state.updateSightWordsLesson.itemToUpdate = {
				isLoading: true,
				data: undefined,
				status: {
					success: false,
					error: false,
				},
			};
		},
		getSightWordsLessonToUpdateSuccess: (
			state: IRegionsConfigurationState,
			action,
		) => {
			state.updateSightWordsLesson.itemToUpdate = {
				isLoading: false,
				data: action.payload.lesson,
				status: {
					success: true,
					error: false,
				},
			};
		},
		getSightWordsLessonToUpdateFailure: (state: IRegionsConfigurationState) => {
			state.updateSightWordsLesson.itemToUpdate = {
				isLoading: false,
				data: undefined,
				status: {
					success: false,
					error: true,
				},
			};
		},
		updateSightWordsLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		updateSightWordsLessonSuccess: (state: IRegionsConfigurationState) => {
			state.updateSightWordsLesson.status = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		updateSightWordsLessonFailure: (state: IRegionsConfigurationState) => {
			state.updateSightWordsLesson.status = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		createTimeLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		createTimeLessonSuccess: (state: IRegionsConfigurationState) => {
			state.createTimeLesson = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		createTimeLessonFailure: (state: IRegionsConfigurationState) => {
			state.createTimeLesson = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		getTimeLessonToUpdate: (state: IRegionsConfigurationState) => {
			state.updateTimeLesson.itemToUpdate = {
				isLoading: true,
				data: undefined,
				status: {
					success: false,
					error: false,
				},
			};
		},
		getTimeLessonToUpdateSuccess: (
			state: IRegionsConfigurationState,
			action,
		) => {
			state.updateTimeLesson.itemToUpdate = {
				isLoading: false,
				data: action.payload.lesson,
				status: {
					success: true,
					error: false,
				},
			};
		},
		getTimeLessonToUpdateFailure: (state: IRegionsConfigurationState) => {
			state.updateTimeLesson.itemToUpdate = {
				isLoading: false,
				data: undefined,
				status: {
					success: false,
					error: true,
				},
			};
		},
		updateTimeLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		updateTimeLessonSuccess: (state: IRegionsConfigurationState) => {
			state.updateTimeLesson.status = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		updateTimeLessonFailure: (state: IRegionsConfigurationState) => {
			state.updateTimeLesson.status = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		searchVocabWords: (state: IRegionsConfigurationState) => {
			state.searchVocabWordsAutoComplete = {
				isLoading: true,
				result: [],
			};
		},
		setSearchVocabWordsAutoCompleteResult: (
			state: IRegionsConfigurationState,
			action,
		) => {
			state.searchVocabWordsAutoComplete = {
				isLoading: false,
				result: action.payload,
			};
		},
		resetSetSearchVocabWordsAutoComplete: (
			state: IRegionsConfigurationState,
		) => {
			state.searchVocabWordsAutoComplete = {
				isLoading: false,
				result: [],
			};
		},

		// --

		createSceneMatchingLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		createSceneMatchingLessonSuccess: (state: IRegionsConfigurationState) => {
			state.createSceneMatchingLesson = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		createSceneMatchingLessonFailure: (state: IRegionsConfigurationState) => {
			state.createSceneMatchingLesson = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},

		getSceneMatchingLessonToUpdate: (state: IRegionsConfigurationState) => {
			state.updateSceneMatchingLesson.itemToUpdate = {
				isLoading: true,
				data: undefined,
				status: {
					success: false,
					error: false,
				},
			};
		},
		getSceneMatchingLessonToUpdateSuccess: (
			state: IRegionsConfigurationState,
			action,
		) => {
			state.updateSceneMatchingLesson.itemToUpdate = {
				isLoading: false,
				data: action.payload.lesson,
				status: {
					success: true,
					error: false,
				},
			};
		},
		getSceneMatchingLessonToUpdateFailure: (
			state: IRegionsConfigurationState,
		) => {
			state.updateSceneMatchingLesson.itemToUpdate = {
				isLoading: false,
				data: undefined,
				status: {
					success: false,
					error: true,
				},
			};
		},
		updateSceneMatchingLesson: (state: IRegionsConfigurationState) => {
			state.isLoading = true;
		},
		updateSceneMatchingLessonSuccess: (state: IRegionsConfigurationState) => {
			state.updateSceneMatchingLesson.status = {
				success: true,
				error: false,
			};
			state.isLoading = false;
		},
		updateSceneMatchingLessonFailure: (state: IRegionsConfigurationState) => {
			state.updateSceneMatchingLesson.status = {
				success: false,
				error: true,
			};
			state.isLoading = false;
		},
	},
});

export const {
	resetRegionsConfigurationData,

	getLessons,
	getLessonsSuccess,
	getLessonsFailure,

	deleteLesson,
	deleteLessonSuccess,
	deleteLessonFailure,
	resetDeleteLessonStatus,

	searchLessons,
	setSearchLessonsAutoCompleteResult,
	resetSetSearchLessonsAutoComplete,

	getNewLessonOrder,
	setNewLessonOrder,

	createVocabLesson,
	createVocabLessonSuccess,
	createVocabLessonFailure,

	getVocabLessonToUpdate,
	getVocabLessonToUpdateSuccess,
	getVocabLessonToUpdateFailure,

	updateVocabLesson,
	updateVocabLessonSuccess,
	updateVocabLessonFailure,

	createVerbLesson,
	createVerbLessonSuccess,
	createVerbLessonFailure,

	getVerbLessonToUpdate,
	getVerbLessonToUpdateSuccess,
	getVerbLessonToUpdateFailure,

	updateVerbLesson,
	updateVerbLessonSuccess,
	updateVerbLessonFailure,

	getSentencesWithVocabLessonWord,
	getSentencesWithVocabLessonWordSuccess,
	getSentencesWithVocabLessonWordFailure,
	resetGetSentenceLessonsWithVocabWord,

	getSentencesWithVocabLessonWords,
	getSentencesWithVocabLessonWordsSuccess,
	getSentencesWithVocabLessonWordsFailure,
	resetGetSentenceLessonsWithVocabLessonWords,
	resetGetSentenceLessonsWithVocabLessonWordsStatus,

	getSentenceLessonsWithOrderLessThanVocabLessonNewOrder,
	getSentenceLessonsWithOrderLessThanVocabLessonNewOrderSuccess,
	getSentenceLessonsWithOrderLessThanVocabLessonNewOrderFailure,
	resetGetSentenceLessonsWithOrderLessThanVocabLessonNewOrder,

	createSentenceLesson,
	createSentenceLessonSuccess,
	createSentenceLessonFailure,

	getSentenceLessonToUpdate,
	getSentenceLessonToUpdateSuccess,
	getSentenceLessonToUpdateFailure,

	updateSentenceLesson,
	updateSentenceLessonSuccess,
	updateSentenceLessonFailure,

	searchSentenceLessonVocabWords,
	setSearchSentenceLessonVocabWordsAutoCompleteResult,
	resetSetSearchSentenceLessonVocabWordsAutoComplete,

	createLexicaryLesson,
	createLexicaryLessonSuccess,
	createLexicaryLessonFailure,

	getLexicaryLessonToUpdate,
	getLexicaryLessonToUpdateSuccess,
	getLexicaryLessonToUpdateFailure,

	updateLexicaryLesson,
	updateLexicaryLessonSuccess,
	updateLexicaryLessonFailure,

	searchLessonItemModifiers,
	setSearchLessonItemModifiersResult,
	resetSearchLessonItemModifiers,

	createLessonItemModifier,
	createLessonItemModifierSuccess,
	createLessonItemModifierFailure,

	updateLessonItemModifier,
	updateLessonItemModifierSuccess,
	updateLessonItemModifierFailure,

	deleteLessonItemModifier,
	deleteLessonItemModifierSuccess,
	deleteLessonItemModifierFailure,

	resetCreateLessonItemModifier,
	resetUpdateLessonItemModifier,
	resetDeleteLessonItemModifier,

	createGrammarLesson,
	createGrammarLessonSuccess,
	createGrammarLessonFailure,

	getGrammarLessonToUpdate,
	getGrammarLessonToUpdateSuccess,
	getGrammarLessonToUpdateFailure,

	updateGrammarLesson,
	updateGrammarLessonSuccess,
	updateGrammarLessonFailure,

	createSortingLesson,
	createSortingLessonSuccess,
	createSortingLessonFailure,

	getSortingLessonToUpdate,
	getSortingLessonToUpdateSuccess,
	getSortingLessonToUpdateFailure,

	updateSortingLesson,
	updateSortingLessonSuccess,
	updateSortingLessonFailure,

	getLessonCategories,
	getLessonCategoriesSuccess,
	getLessonCategoriesFailure,

	createLessonCategory,
	createLessonCategorySuccess,
	createLessonCategoryFailure,

	resetCreateLessonCategory,

	updateLessonCategory,
	updateLessonCategorySuccess,
	updateLessonCategoryFailure,
	resetUpdateLessonCategory,

	deleteLessonCategory,
	deleteLessonCategorySuccess,
	deleteLessonCategoryFailure,

	resetDeleteLessonCategory,

	searchLessonCategories,
	setSearchLessonCategoriesResult,
	resetSearchLessonCategories,

	createBusyPictureLesson,
	createBusyPictureLessonSuccess,
	createBusyPictureLessonFailure,

	getBusyPictureLessonToUpdate,
	getBusyPictureLessonToUpdateSuccess,
	getBusyPictureLessonToUpdateFailure,

	updateBusyPictureLesson,
	updateBusyPictureLessonSuccess,
	updateBusyPictureLessonFailure,

	createNumberCombinationLesson,
	createNumberCombinationLessonSuccess,
	createNumberCombinationLessonFailure,

	getNumberCombinationLessonToUpdate,
	getNumberCombinationLessonToUpdateSuccess,
	getNumberCombinationLessonToUpdateFailure,

	updateNumberCombinationLesson,
	updateNumberCombinationLessonSuccess,
	updateNumberCombinationLessonFailure,

	createSightWordsLesson,
	createSightWordsLessonSuccess,
	createSightWordsLessonFailure,

	getSightWordsLessonToUpdate,
	getSightWordsLessonToUpdateSuccess,
	getSightWordsLessonToUpdateFailure,

	updateSightWordsLesson,
	updateSightWordsLessonSuccess,
	updateSightWordsLessonFailure,

	createTimeLesson,
	createTimeLessonSuccess,
	createTimeLessonFailure,

	getTimeLessonToUpdate,
	getTimeLessonToUpdateSuccess,
	getTimeLessonToUpdateFailure,

	updateTimeLesson,
	updateTimeLessonSuccess,
	updateTimeLessonFailure,

	createSceneMatchingLesson,
	createSceneMatchingLessonSuccess,
	createSceneMatchingLessonFailure,

	getSceneMatchingLessonToUpdate,
	getSceneMatchingLessonToUpdateSuccess,
	getSceneMatchingLessonToUpdateFailure,

	updateSceneMatchingLesson,
	updateSceneMatchingLessonSuccess,
	updateSceneMatchingLessonFailure,

	searchVocabWords,
	setSearchVocabWordsAutoCompleteResult,
	resetSetSearchVocabWordsAutoComplete,
} = regionsConfigurationSlice.actions;

export const regionsConfigurationStateSelector = (
	state: RootStateOrAny,
): IRegionsConfigurationState => state[REGIONS_CONFIGURATION_KEY_IN_STORE];

export default regionsConfigurationSlice;
